/**
 * The receiver: the audio graph the player listens to.
 *
 *   noise ──► static high-pass ──► static low-pass ──► noise gain ──► mixer "static" bus
 *
 *   transmission ──► band-pass ──► low-pass ──► signal gain ────────► mixer "signal" bus
 *   heterodyne osc ──► whistle gain ────────────────────────────────►
 *
 * The dial drives every one of those parameters through `tuning.ts`; nothing here decides what
 * something should sound like, it only applies the model. Master gain, staging and limiting
 * belong to the mixer, not here.
 */
import { audioContext, rampTo, resumeAudio } from './context.js';
import type { Mixer } from './mixer.js';
import { createNoiseSource, type NoiseSource } from './noise.js';
import { createTransmission, type Transmission } from './transmission.js';
import { bandTexture, dialScrub, signalShape, type SignalShape } from './tuning.js';
import { SfericEmitter, propagationFade } from './atmospherics.js';
import type { ActiveSignal } from '../world/matrix.js';

/** How quickly filter/gain moves follow the dial. Short enough to feel physical, long enough
 *  that a fast sweep doesn't zipper. */
const FOLLOW_SECONDS = 0.06;

/**
 * How often the receiver re-asks the world what is on the air.
 *
 * The dial is not the only thing that changes what a player hears: windows open and close and
 * signals drift while the dial sits still. A quarter-second poll is far below the rate at which
 * anything in the schedule actually moves, and costs one matrix resolution.
 */
const WORLD_POLL_MS = 250;

/**
 * Asks the world what is receivable at a frequency right now. The receiver holds one of these
 * instead of a matrix so it stays ignorant of scheduling — Chapter 5's ghosts and Chapter 6's
 * roaming NPCs can be folded into the same answer without touching the audio chain.
 */
export type SignalResolver = (frequencyKhz: number) => ActiveSignal | null;

export interface ReceiverOptions {
  mixer: Mixer;
  frequencyKhz: number;
  /** Omit for a receiver on a dead band — every frequency resolves to static. */
  resolve?: SignalResolver;
}

export class Receiver {
  private readonly mixer: Mixer;
  private noise: NoiseSource | null = null;
  private noiseGain: GainNode | null = null;
  private staticHighPass: BiquadFilterNode | null = null;
  private staticLowPass: BiquadFilterNode | null = null;
  private transmission: Transmission | null = null;
  private signalBandPass: BiquadFilterNode | null = null;
  private signalLowPass: BiquadFilterNode | null = null;
  private signalGain: GainNode | null = null;
  private heterodyne: OscillatorNode | null = null;
  private heterodyneGain: GainNode | null = null;
  private sferics: SfericEmitter | null = null;
  /** Player-set atmospherics intensity, 0–1. Survives power cycles. */
  private atmospherics = 1;

  private starting: Promise<void> | null = null;
  private frequency: number;
  private resolve: SignalResolver;
  /** The signal currently wired into the transmission path, if any. */
  private tuned: ActiveSignal | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private signalListeners = new Set<(signal: ActiveSignal | null) => void>();
  private powered = false;
  private scrub = 0;
  private lastTuneAt = 0;
  private strengthListeners = new Set<(shape: SignalShape) => void>();

  constructor(options: ReceiverOptions) {
    this.mixer = options.mixer;
    this.frequency = options.frequencyKhz;
    this.resolve = options.resolve ?? (() => null);
  }

  /** Swap in a different world — used when a content pack finishes loading. */
  setResolver(resolve: SignalResolver): void {
    this.resolve = resolve;
    this.refreshTunedSignal();
  }

  get isPowered(): boolean {
    return this.powered;
  }

  get noiseMode(): 'worklet' | 'buffer' | null {
    return this.noise?.mode ?? null;
  }

  get frequencyKhz(): number {
    return this.frequency;
  }

  /**
   * Current signal shape — the UI meter reads this even while the receiver is off.
   * With nothing on the air at this dial position, the shape is a zeroed one: pure noise floor.
   */
  get shape(): SignalShape {
    const signal = this.tuned;
    if (!signal) return signalShape(this.frequency, this.frequency + 1e9, 1);
    return signalShape(this.frequency, signal.frequencyKhz, signal.widthKhz);
  }

  /** What the receiver is currently picking up, if anything. */
  get tunedSignal(): ActiveSignal | null {
    return this.tuned;
  }

  onShapeChange(listener: (shape: SignalShape) => void): () => void {
    this.strengthListeners.add(listener);
    listener(this.shape);
    return () => this.strengthListeners.delete(listener);
  }

  /** Fires when the receiver locks onto a different signal, or loses one. */
  onSignalChange(listener: (signal: ActiveSignal | null) => void): () => void {
    this.signalListeners.add(listener);
    listener(this.tuned);
    return () => this.signalListeners.delete(listener);
  }

  async powerOn(): Promise<void> {
    if (this.powered) return;
    if (this.starting) return this.starting;

    this.starting = (async () => {
      await resumeAudio();
      const ctx = audioContext();
      const staticBus = this.mixer.bus('static');
      const signalBus = this.mixer.bus('signal');

      // --- static path ---------------------------------------------------------------
      const staticHighPass = ctx.createBiquadFilter();
      staticHighPass.type = 'highpass';
      staticHighPass.frequency.value = 120;

      const staticLowPass = ctx.createBiquadFilter();
      staticLowPass.type = 'lowpass';
      staticLowPass.frequency.value = 4200;
      staticLowPass.Q.value = 0.6;

      const noiseGain = ctx.createGain();
      noiseGain.gain.value = 1;

      const noise = await createNoiseSource(bandTexture(this.frequency));
      noise.node
        .connect(staticHighPass)
        .connect(staticLowPass)
        .connect(noiseGain)
        .connect(staticBus);

      // Crackle joins the static *after* its band-shaping filters but *before* the duck, so a
      // burst is not squeezed through this frequency's passband (lightning is broadband) yet
      // still gets out of the way of a transmission and breathes with the propagation fade.
      const sferics = new SfericEmitter({ destination: noiseGain });
      sferics.setFrequency(this.frequency);
      sferics.setLevel(this.atmospherics);
      sferics.start();

      // --- transmission path ---------------------------------------------------------
      const signalBandPass = ctx.createBiquadFilter();
      signalBandPass.type = 'bandpass';
      signalBandPass.frequency.value = 900;
      signalBandPass.Q.value = 6;

      const signalLowPass = ctx.createBiquadFilter();
      signalLowPass.type = 'lowpass';
      signalLowPass.frequency.value = 2000;

      const signalGain = ctx.createGain();
      signalGain.gain.value = 0;

      // The transmission itself is attached by refreshTunedSignal() once the world says there is
      // something here; the filter chain exists either way so the graph never gets rebuilt.
      signalBandPass.connect(signalLowPass).connect(signalGain).connect(signalBus);

      // --- heterodyne whistle --------------------------------------------------------
      const heterodyne = ctx.createOscillator();
      heterodyne.type = 'sine';
      heterodyne.frequency.value = 800;
      const heterodyneGain = ctx.createGain();
      heterodyneGain.gain.value = 0;
      heterodyne.connect(heterodyneGain).connect(signalBus);
      heterodyne.start();

      this.noise = noise;
      this.noiseGain = noiseGain;
      this.staticHighPass = staticHighPass;
      this.staticLowPass = staticLowPass;
      this.signalBandPass = signalBandPass;
      this.signalLowPass = signalLowPass;
      this.signalGain = signalGain;
      this.heterodyne = heterodyne;
      this.heterodyneGain = heterodyneGain;
      this.sferics = sferics;
      this.powered = true;

      // Attach whatever is on the air here, and set every parameter *before* the buses come up,
      // so nothing is heard mid-move.
      this.refreshTunedSignal();
      this.applyTuning(0.01);

      // Windows open and close and signals drift while the dial sits still.
      this.pollTimer = setInterval(() => this.refreshTunedSignal(), WORLD_POLL_MS);
      // A receiver warming, not a switch clicking.
      this.mixer.setBusLevel('static', 1, 0.5);
      this.mixer.setBusLevel('signal', 1, 0.5);
    })();

    try {
      await this.starting;
    } finally {
      this.starting = null;
    }
  }

  /**
   * Duck static and signal while the player is transmitting, like real half-duplex radio equipment.
   */
  setTransmitting(isTransmitting: boolean): void {
    if (!this.powered) return;
    this.mixer.setBusLevel('static', isTransmitting ? 0.15 : 1, 0.05);
    this.mixer.setBusLevel('signal', isTransmitting ? 0.15 : 1, 0.05);
  }

  powerOff(): void {
    if (!this.powered) return;
    const noise = this.noise;
    const transmission = this.transmission;
    const heterodyne = this.heterodyne;

    /*
     * Everything that is wired into a mixer bus, so it can be *unwired*.
     *
     * Dropping the references (below) is not enough: a node that is still connected to a bus is
     * reachable from the audio destination, so the browser can never collect it. Before this,
     * every power cycle orphaned a whole filter chain — static high-pass, low-pass, gain, the
     * transmission chain and the heterodyne — permanently attached to the mixer and permanently
     * silent. Measured over a session, that was about ten nodes per cycle that never came back.
     */
    const wired = [
      this.staticHighPass,
      this.staticLowPass,
      this.noiseGain,
      this.signalBandPass,
      this.signalLowPass,
      this.signalGain,
      this.heterodyneGain,
    ];

    this.sferics?.stop();
    this.sferics = null;
    this.powered = false;
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.tuned = null;
    for (const listener of this.signalListeners) listener(null);
    this.noise = null;
    this.noiseGain = null;
    this.staticHighPass = null;
    this.staticLowPass = null;
    this.transmission = null;
    this.signalBandPass = null;
    this.signalLowPass = null;
    this.signalGain = null;
    this.heterodyne = null;
    this.heterodyneGain = null;

    // Fade the buses out first, then tear the nodes down — stopping a live source clicks.
    this.mixer.setBusLevel('static', 0, 0.25);
    this.mixer.setBusLevel('signal', 0, 0.25);
    setTimeout(() => {
      noise?.dispose();
      transmission?.stop();
      try {
        heterodyne?.stop();
      } catch {
        /* already stopped */
      }
      // Only now, once the buses have faded and the sources have stopped: disconnecting a live
      // node mid-sample is exactly the step discontinuity the fade above exists to avoid.
      for (const node of wired) node?.disconnect();
    }, 350);
  }

  /** Move the dial. Safe to call at pointer-move rate. */
  setFrequency(khz: number): void {
    const now = performance.now();
    const elapsed = this.lastTuneAt ? (now - this.lastTuneAt) / 1000 : 0;
    const delta = khz - this.frequency;
    if (elapsed > 0 && elapsed < 0.5) {
      // Decay towards the new scrub level so a single jump doesn't latch the effect on.
      this.scrub = Math.max(this.scrub * 0.6, dialScrub(delta / elapsed));
    }
    this.lastTuneAt = now;
    this.frequency = khz;

    // Moving the dial can bring a different transmitter into range, so this is a full re-resolve
    // rather than just a filter update.
    this.refreshTunedSignal();
  }

  setVolume(value: number): void {
    this.mixer.setVolume(value);
  }

  /**
   * How much atmospheric crackle the player wants, 0–1.
   *
   * Kept on the receiver rather than the emitter so it survives a power cycle: a player who
   * turned the lightning down should not have it come back when they switch the set off and on.
   */
  setAtmospherics(level: number): void {
    this.atmospherics = Math.max(0, Math.min(1, level));
    this.sferics?.setLevel(this.atmospherics);
  }

  getAtmospherics(): number {
    return this.atmospherics;
  }

  dispose(): void {
    this.strengthListeners.clear();
    this.signalListeners.clear();
    this.powerOff();
  }

  /**
   * Ask the world what is here and, if it is a different transmission from the one currently
   * wired up, swap it.
   *
   * The swap is by signal identity, not by frequency: a drifting signal stays the same
   * transmission as it moves, and only tuning away from it (or its window closing) tears it
   * down. Rebuilding the audio on every dial step would restart the numbers station's phrase
   * every kHz, which is both wrong and expensive.
   */
  private refreshTunedSignal(): void {
    const next = this.powered ? this.resolve(this.frequency) : null;
    const changed = next?.signal.id !== this.tuned?.signal.id;

    if (changed && this.signalBandPass) {
      this.transmission?.stop();
      this.transmission = null;

      if (next) {
        const transmission = createTransmission(next.signal.source);
        transmission.output.connect(this.signalBandPass);
        this.transmission = transmission;
      }
    }

    this.tuned = next;
    if (changed) {
      for (const listener of this.signalListeners) listener(next);
    }

    // Drift and window changes move the shape even when the signal identity is unchanged.
    this.applyTuning();
    const shape = this.shape;
    for (const listener of this.strengthListeners) listener(shape);
  }

  private applyTuning(seconds = FOLLOW_SECONDS): void {
    if (!this.powered || !this.noise) return;

    const texture = bandTexture(this.frequency);
    const shape = this.shape;

    // Static: colour and passband follow the band texture; the scrub lift rides on top.
    this.noise.setLevels(
      {
        white: texture.white + this.scrub,
        pink: texture.pink,
        brown: texture.brown,
      },
      seconds,
    );
    rampTo(this.staticHighPass!.frequency, texture.lowCutHz, seconds);
    rampTo(this.staticLowPass!.frequency, texture.highCutHz, seconds);

    // The band breathes: propagation swells and sinks the whole noise floor over tens of seconds,
    // on top of the duck. Ramped over at least the world-poll interval, because this is otherwise
    // only recomputed every 250 ms and a short ramp would leave a stair-step between polls.
    const fade = propagationFade(this.frequency, audioContext().currentTime);
    rampTo(this.noiseGain!.gain, shape.noiseDuck * fade, Math.max(seconds, WORLD_POLL_MS / 1000));
    this.sferics?.setFrequency(this.frequency);

    // Transmission: arrives through the filters, not the volume knob.
    rampTo(this.signalBandPass!.frequency, shape.bandpassHz, seconds);
    rampTo(this.signalBandPass!.Q, shape.bandpassQ, seconds);
    rampTo(this.signalLowPass!.frequency, shape.lowpassHz, seconds);
    rampTo(this.signalGain!.gain, shape.gain, seconds);

    // The whistle that makes tuning feel like tuning.
    rampTo(this.heterodyne!.frequency, shape.heterodyneHz, seconds);
    rampTo(this.heterodyneGain!.gain, shape.heterodyneGain, seconds);

    // The scrub lift is a transient: bleed it away between moves.
    this.scrub *= 0.72;
    if (this.scrub < 0.01) this.scrub = 0;
  }
}
